import {UI, type PartialOptions} from "../../stem-core/ui/UIBase";
import {EndpointPaginator} from "../../stem-core/state/pagination/EndpointPaginator";
import {Payment} from "../../client/state/PaymentStore";
import {SimpleTable} from "../ui/SimpleTable";
import {Money} from "../../stem-core/localization/Money.js";
import {StemDate} from "../../stem-core/time/Date";
import {Button} from "../../stem-core/ui/button/Button";
import {ConfirmationModal} from "../../blinkpay/ui/ConfirmationModal";
import {autoredraw} from "../../stem-core/decorators/AutoRedraw";
import {MONEY_COLUMN} from "./theme/TableStyle";
import {DashboardLabel} from "./DashboardLabel";
import {Level} from "../../stem-core/ui/Constants";
import {MerchantReportType} from "../../client/state/merchant/MerchantReportStore.js";
import type {MerchantUser} from "../../client/state/merchant/MerchantUserStore";
import type {ColumnTuple} from "../../stem-core/base/ColumnHandler";


interface MerchantRefundPaymentConfirmationOptions {
    payment?: Payment;
}

class MerchantRefundPaymentConfirmation extends ConfirmationModal<MerchantRefundPaymentConfirmationOptions> {
    getDefaultOptions(): PartialOptions<MerchantRefundPaymentConfirmation> {
        return {
            ...super.getDefaultOptions(),
            title: "Refund payment",
            description: "Do you want to refund this payment back to the user though the payment method they originally used? This action is not reversible.",
            cancelLabel: "Cancel",
            confirmLabel: "Refund payment",
            confirmAction: () => this.refundPayment(),
        };
    }

    render() {
        const {payment} = this.options;

        return [
            // TODO: support to cancel the subscription here as well.
            <div>Note that this merely refunds the payment, and resource ownership (e.g. canceling the subscription) is handled manually.</div>,
        ]
    }

    async refundPayment() {
        const {payment} = this.options;

        await this.makeRequest("/merchant/revert_payment/", {
            paymentId: payment.id,
        });
    }
}


interface PaymentRefundButtonOptions {
    payment?: Payment;
}

@autoredraw
class PaymentRefundButton extends UI.Element<PaymentRefundButtonOptions> {
    render() {
        const {payment} = this.options;
        if (payment.isRefunded()) {
            return <DashboardLabel level={Level.INFO}>Refunded</DashboardLabel>
        }

        return <Button label="Refund" onClick={() => MerchantRefundPaymentConfirmation.show({payment})} />
    }
}

interface MerchantUserPaymentsTableOptions {
    merchantUser?: MerchantUser;
}

export class MerchantUserPaymentsTable extends UI.Element<MerchantUserPaymentsTableOptions> {
    paginator = new EndpointPaginator(Payment, "/merchant/get_payments/");

    render() {
        const {merchantUser} = this.options;
        const {paginator} = this;
        paginator.updateFilter({
            merchantId: merchantUser.merchantId,
            userFilter: {
                type: MerchantReportType.USERS,
                userIds: [merchantUser.userId],
            },
        });

        const columns: ColumnTuple<Payment>[] = [
            ["Date", payment => new StemDate(payment.createdAt)],
            ["Amount", payment => new Money(payment), MONEY_COLUMN],
            ["Type", payment => payment.getType()],
            ["Actions", payment => <PaymentRefundButton payment={payment} />],
        ]

        return <SimpleTable<Payment> columns={columns} paginator={paginator}/>;
    }
}
